import { notifications } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import {
  AttachmentDisplay,
  AttachmentInfo,
} from 'src/components/Messages/AttachmentsPanel';
import { validateAttachments } from 'src/components/ReachProSupport/attachmentValidation';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack } from 'src/core/ui';
import { AddFileIcon } from 'src/svgs/AddFileIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { VenueMapReportType } from 'src/WebApiController';

import { Modal, ModalBody, ModalFooter, ModalHeader } from '../Modal';
import * as styles from './VenueMapReportModal.css';
import {
  useEventProofUrl,
  useReportVenueMapIssue,
} from './VenueMapReportModal.hooks';

const toAttachmentInfo = (f: File): AttachmentInfo => ({
  attachmentId: f.name,
  name: f.name,
  size: f.size,
});

export type VenueMapReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The events the report covers (single map view = one; FR4 bulk = many, same config). */
  eventIds: number[];
  /** Venue configuration id the events use (the map being reported). */
  venueConfigId: number | null;
  /** Preset the report type (e.g. the bulk menu already chose incorrect/missing). */
  initialReportType?: VenueMapReportType;
  /** Hide the incorrect/missing toggle when the type is fixed by the caller. */
  hideReportTypeToggle?: boolean;
  onSubmitted?: () => void;
};

export const VenueMapReportModal = ({
  isOpen,
  onClose,
  eventIds,
  venueConfigId,
  initialReportType,
  hideReportTypeToggle,
  onSubmitted,
}: VenueMapReportModalProps) => {
  const [reportType, setReportType] = useState<VenueMapReportType>(
    initialReportType ?? VenueMapReportType.IncorrectMap
  );

  // Sync the preset type each time the modal opens (the modal instance persists
  // across opens, so useState alone would not pick up a changed initial type).
  useEffect(() => {
    if (isOpen) {
      setReportType(initialReportType ?? VenueMapReportType.IncorrectMap);
    }
  }, [isOpen, initialReportType]);
  const [description, setDescription] = useState('');
  const [reportUrl, setReportUrl] = useState('');
  const [urlDirty, setUrlDirty] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const report = useReportVenueMapIssue();
  const descriptionPlaceholder = useContent(
    ContentId.ReportMapIssueDescriptionPlaceholder
  );
  const proofUrlPlaceholder = useContent(ContentId.ReportProofUrlPlaceholder);
  const submittedMessage = useContent(ContentId.ReportMapIssueSubmitted);

  // FR6: prefill the proof URL for a single event, unless the seller has edited it.
  const { proofUrl } = useEventProofUrl(eventIds, isOpen);
  useEffect(() => {
    if (!urlDirty && proofUrl) {
      setReportUrl(proofUrl);
    }
  }, [proofUrl, urlDirty]);

  const isValid = description.trim() !== '' && eventIds.length > 0;

  const reset = () => {
    setReportType(initialReportType ?? VenueMapReportType.IncorrectMap);
    setDescription('');
    setReportUrl('');
    setUrlDirty(false);
    setFiles([]);
    setAttachmentError(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addFiles = (incoming: File[]) => {
    const result = validateAttachments(files, incoming);
    if (result.error) {
      setAttachmentError(result.error.message);
      return;
    }
    setAttachmentError(undefined);
    setFiles((prev) => [...prev, ...result.accepted]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) {
      addFiles(selected);
    }
    e.target.value = '';
  };

  const handleDeleteAttachment = (attachment: AttachmentInfo) => {
    setFiles((prev) => prev.filter((f) => f.name !== attachment.attachmentId));
    setAttachmentError(undefined);
  };

  const handleSubmit = async () => {
    if (!isValid || report.isPending) {
      return;
    }
    const result = await report.mutateAsync({
      reportType,
      eventIds,
      venueConfigId,
      description: description.trim(),
      primaryUrl: reportUrl.trim(),
      attachments: files,
    });
    if (!result) {
      return;
    }
    notifications.show({ message: submittedMessage, color: 'green' });
    onSubmitted?.();
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      unmountOnClose
      centered
      size="sm"
    >
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerBar}>
          <h5 className={styles.headerText}>
            <Content id={ContentId.ReportMapIssue} />
            {eventIds.length > 1 ? ` (${eventIds.length})` : ''}
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={styles.modalBody}>
          <Stack direction="column" gap="l">
            {!hideReportTypeToggle && (
              <>
                <div className={styles.fieldLabel}>
                  <Content id={ContentId.Type} />
                </div>
                <Stack direction="row" gap="s">
                  <Button
                    variant={
                      reportType === VenueMapReportType.IncorrectMap
                        ? 'regular'
                        : 'outline'
                    }
                    disabled={report.isPending}
                    onClick={() =>
                      setReportType(VenueMapReportType.IncorrectMap)
                    }
                  >
                    <Content id={ContentId.ReportMapIssueIncorrect} />
                  </Button>
                  <Button
                    variant={
                      reportType === VenueMapReportType.MissingMap
                        ? 'regular'
                        : 'outline'
                    }
                    disabled={report.isPending}
                    onClick={() => setReportType(VenueMapReportType.MissingMap)}
                  >
                    <Content id={ContentId.ReportMapIssueMissing} />
                  </Button>
                </Stack>
              </>
            )}

            <div className={styles.fieldLabel}>
              <Content id={ContentId.Description} />
              <span className={styles.required}>*</span>
            </div>
            <PosTextArea
              value={description}
              disabled={report.isPending}
              placeholder={descriptionPlaceholder}
              maxLength={1000}
              rows={4}
              onChange={(e) => setDescription(e.target.value)}
            />

            <PosTextField
              value={reportUrl}
              disabled={report.isPending}
              placeholder={proofUrlPlaceholder}
              maxLength={2048}
              onChange={(e) => {
                setReportUrl(e.target.value);
                setUrlDirty(true);
              }}
            />

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.pdf"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            <button
              type="button"
              className={styles.browseFilesButton}
              disabled={report.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              <AddFileIcon size="16px" />
              <Content id={ContentId.BrowseFiles} />
            </button>
            {files.length > 0 && (
              <Stack direction="row" gap="s" flexWrap="wrap">
                {files.map((f) => (
                  <AttachmentDisplay
                    key={f.name}
                    attachmentInfo={toAttachmentInfo(f)}
                    showDelete
                    onDeleteClicked={handleDeleteAttachment}
                  />
                ))}
              </Stack>
            )}
            {attachmentError && (
              <span className={styles.attachmentError}>{attachmentError}</span>
            )}
          </Stack>
        </div>
      </ModalBody>
      <ModalFooter>
        <CancelButton textContentId={ContentId.Cancel} onClick={handleClose} />
        <OkButton
          variant="regular"
          textContentId={ContentId.Submit}
          onClick={handleSubmit}
          disabled={!isValid || report.isPending}
        />
      </ModalFooter>
    </Modal>
  );
};
